import {
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Grid,
} from '@mui/material'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import useMediaQuery from '@mui/material/useMediaQuery'
import { RTFODataAnalyticsChart } from './RTFODataAnalyticsChart'
import { useEffect, useMemo, useState } from 'react'
import { getMetricParamName, getMetricUnits } from './RTFOMetricsPanel'

import { scaleSequential } from 'd3-scale'
import { interpolateTurbo } from 'd3-scale-chromatic'
import { Cancel, Check } from '@mui/icons-material'
import { useTheme } from '@mui/styles'

export const RTFODataAnalytics = ({
  darkMode,
  currentRTFO,
  allPadsMetrics,
  isLoadingPadsMetrics,
}) => {
  const theme = useTheme()
  const downMedium = useMediaQuery(theme.breakpoints.down('md'))

  const allWells = useMemo(() => {
    let allWells = []

    if (!allPadsMetrics || Object.keys(allPadsMetrics)?.length === 0)
      return allWells

    for (const padName of Object.keys(allPadsMetrics)) {
      for (const stageName of Object.keys(allPadsMetrics?.[padName]?.metrics)) {
        const wellName = stageName?.split('_')?.slice(0, 2)?.join('_')
        if (allWells?.indexOf(wellName) === -1) {
          allWells.push(wellName)
        }
      }
    }

    return allWells
  }, [allPadsMetrics])

  const [displayPads, setDisplayPads] = useState(
    allPadsMetrics ? Object.keys(allPadsMetrics) : [],
  )
  const [displayWells, setDisplayWells] = useState(allWells)
  const [metricX, setMetricX] = useState('Stage Time')
  const [metricY, setMetricY] = useState('Fluid Intensity')
  const [metricZ, setMetricZ] = useState('ISIP Surf.')
  const [metricSize, setMetricSize] = useState('None')
  const [xAxisType, setXAxisType] = useState('linear')
  const [yAxisType, setYAxisType] = useState('linear')
  const [showWellTrendlines, setShowWellTrendlines] = useState('hide')
  const [showCDF, setShowCDF] = useState('show')

  useEffect(() => {
    allPadsMetrics && setDisplayPads(Object.keys(allPadsMetrics))
    setDisplayWells(allWells)
  }, [allPadsMetrics, allWells])

  const [zMinMax, setZMinMax] = useState(() => {
    if (metricZ === 'None') return []
    // Calculate min and max range for Z axis metric
    const combinedZ = []
    for (const padIndex in displayPads) {
      const padName = displayPads[padIndex]
      for (const stageName in allPadsMetrics[padName]?.metrics) {
        if (!displayWells.includes(stageName.split('_')[1])) continue
        const zValue =
          allPadsMetrics[padName]?.metrics?.[stageName]?.[
            getMetricParamName(metricZ)
          ]

        if (!isNaN(zValue) && zValue > 0) {
          combinedZ.push(zValue)
        }
      }
    }

    const min = Math.min(...combinedZ)
    const max = Math.max(...combinedZ)

    if (!isNaN(min) && isFinite(min) && !isNaN(max) && isFinite(max)) {
      return [parseFloat(min?.toFixed(2)), parseFloat(max?.toFixed(2))]
    } else {
      return []
    }
  })

  const [sizeMinMax, setSizeMinMax] = useState(() => {
    // Calculate min and max range for size axis metric
    const combinedSize = []
    for (const padIndex in displayPads) {
      const padName = displayPads[padIndex]

      for (const stageName in allPadsMetrics[padName]?.metrics) {
        const sizeValue =
          allPadsMetrics[padName]?.metrics?.[stageName]?.[
            getMetricParamName(metricSize)
          ] || 0

        if (!isNaN(sizeValue) && sizeValue >= 0) {
          combinedSize.push(sizeValue)
        }
      }
    }

    const min = Math.min(...combinedSize)
    const max = Math.max(...combinedSize)

    if (!isNaN(min) && isFinite(min) && !isNaN(max) && isFinite(max)) {
      return [parseFloat(min?.toFixed(2)), parseFloat(max?.toFixed(2))]
    } else {
      return []
    }
  })

  useEffect(() => {
    if (metricZ === 'None') return setZMinMax([])
    // Calculate min and max range for Z axis metric
    const combinedZ = []
    for (const padIndex in displayPads) {
      const padName = displayPads[padIndex]

      for (const stageName in allPadsMetrics[padName]?.metrics) {
        const zValue = Number(
          allPadsMetrics[padName]?.metrics?.[stageName]?.[
            getMetricParamName(metricZ)
          ],
        )

        if (!isNaN(zValue) && zValue > 0) {
          combinedZ.push(zValue)
        }
      }
    }

    const min = Math.min(...combinedZ)
    const max = Math.max(...combinedZ)

    if (!isNaN(min) && isFinite(min) && !isNaN(max) && isFinite(max)) {
      setZMinMax([parseFloat(min?.toFixed(2)), parseFloat(max?.toFixed(2))])
    }

    return () => {}
  }, [metricZ, allPadsMetrics, displayPads])

  useEffect(() => {
    if (metricSize === 'None') {
      setSizeMinMax([])
      return
    }

    // Calculate min and max range for Z axis metric
    const combinedSize = []
    for (const padIndex in displayPads) {
      const padName = displayPads[padIndex]

      for (const stageName in allPadsMetrics[padName]?.metrics) {
        const sizeValue =
          Number(
            allPadsMetrics[padName]?.metrics?.[stageName]?.[
              getMetricParamName(metricSize)
            ],
          ) || 0

        if (!isNaN(sizeValue) && sizeValue > 0) {
          combinedSize.push(sizeValue)
        }
      }
    }

    if (combinedSize?.length === 0) {
      setSizeMinMax([])
      return
    }

    const min = Math.min(...combinedSize)
    const max = Math.max(...combinedSize)

    if (!isNaN(min) && isFinite(min) && !isNaN(max) && isFinite(max)) {
      setSizeMinMax([parseFloat(min?.toFixed(2)), parseFloat(max?.toFixed(2))])
    }

    return () => {}
  }, [metricSize, allPadsMetrics, displayPads])

  const allMetrics = {
    None: ['None'],
    'Stage Information': [
      'Stage Number',
      'Stage Time',
      '# Clusters',
      'Cluster Spacing',
      'Stage Length',
      'Average Depth',
    ],
    'Pumping Information': [
      'Time to Pre Sand',
      'Propp. Placement Eff.',
      'Fluid Intensity',
      'Sand Per Fluid',
      'Fluid Per Sand',
      'DFR Per Sand',
      'DFR Per Fluid',
      'LFR Per Sand',
      'LFR Per Fluid',
      'Total DFR',
      'Total LFR',
      'Clean Vol. to Pre Sand',
      'Total Slurry Volume',
      'Total Clean Volume',
      'Total Produced Volume',
      'Overflush Vol.',
      'TPM Planned',
      'TPM Actual',
      'Total Proppant',
      'Average Pumping Pr.',
      'Average Pumping Rate',
      'Well Swap Time',
    ],
    ISIP: ['Breakdown Pressure', 'ISIP Surf.', 'ISIP BH', 'Frac Gradient'],
    'Stepdown Model': [
      'Pre Sand Pressure',
      'Pre Sand Rate',
      'Pre Sand Pnwb',
      'Pre Sand Pperf',
      'Flush Pperf',
      'Pre Sand Open Perfs',
      'Flush Open Perfs',
      'Perf Efficiency',
      'Rate Drop Events',
      'Plug Slip Events',
      'Sandoff Events',
      'Isolation Issue Events',
      'Equipment Issue Events',
      'Lost Suction Events',
      'Other Events',
      'Fracfeed Events',
    ],
    'Frac Model': ['Seismicity Risk', 'A*sqrt(k)', 'Frac Area', 'Half Length'],
    Seismicity: ['Local Magnitude'],
  }

  const allPadsAreas = allPadsMetrics ? [...new Set(Object.keys(allPadsMetrics)?.map(padName => allPadsMetrics?.[padName]?.area))] : []

  return (
    <Paper
      elevation={0}
      sx={{ boxShadow: 1 }}
      style={{
        padding: 20,
        margin: '0 20px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        backgroundColor:
          theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
      }}
    >
      <Typography
        variant="h6"
        style={{ marginBottom: '20px' }}
      >
        Data Analytics
      </Typography>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {currentRTFO ? (
          <Grid
            container
            sx={{
              [theme.breakpoints.up('sm')]: { height: '750px' },
              display: 'flex',
            }}
          >
            <Grid
              item
              xs={12}
              sm={12}
              md={4}
              lg={3}
              pt={2}
              order={downMedium ? 2 : 1}
              sx={{
                [theme.breakpoints.up('sm')]: { height: '100%' },
              }}
              style={{
                paddingRight: 20,
                display: 'flex',
                flexDirection: 'column',
                borderRight: '1px solid lightgrey',
              }}
            >
              <Typography variant="h5">Settings</Typography>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flexStart',
                  marginBottom: 20,
                  marginTop: 20,
                }}
              >
                <Typography>Pads</Typography>
                {/* <NestedSelect /> */}
                <FormControl>
                  <Select
                    value={displayPads}
                    size="small"
                    multiple
                    renderValue={(selected) => selected.join(', ')}
                    style={{
                      width: '100%',
                      height: 40,
                      backgroundColor:
                        theme?.palette?.elevationOne?.[
                          darkMode ? 'dark' : 'light'
                        ],
                    }}
                    disabled={isLoadingPadsMetrics}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: downMedium ? 250 : 600,
                          backgroundColor:
                            theme?.palette?.elevationOne?.[
                              darkMode ? 'dark' : 'light'
                            ],
                        },
                      },
                    }}
                  >
                    <MenuItem
                      value={'selectAll'}
                      onClick={() => handlePadCheckClick('selectAll')}
                    >
                      <Checkbox checked={true} />
                      <ListItemText primary={'Select All'} />
                    </MenuItem>

                    <Divider />

                    <MenuItem
                      value={'deselectAll'}
                      onClick={() => handlePadCheckClick('deselectAll')}
                    >
                      <Checkbox checked={false} />
                      <ListItemText primary={'Deselect All'} />
                    </MenuItem>

                    <Divider />
                    
                    {allPadsAreas?.map(area => {
                      return (
                        <div key={area}>
                          <Typography variant='caption' style={{marginLeft: 10}}>{area || 'Area'}</Typography>
                          {Object.keys(allPadsMetrics)?.filter(padName => allPadsMetrics[padName]?.area === area)?.map((padName, i) => {
                          return (
                            <div key={padName}>
                              <Accordion
                                elevation={0}
                                style={{
                                  borderRadius: 0,
                                  backgroundColor: 'transparent',
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon />}
                                  aria-controls="panel1-content"
                                  id="panel1-header"
                                >
                                  <MenuItem
                                    value={padName}
                                    style={{ width: '100%' }}
                                    onClick={() => handlePadCheckClick(padName)}
                                  >
                                    <Checkbox
                                      checked={displayPads.indexOf(padName) > -1}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handlePadCheckClick(padName, true)
                                      }}
                                    />
                                    <ListItemText primary={padName} />
                                  </MenuItem>
                                </AccordionSummary>
                                <AccordionDetails>
                                  {allWells
                                    ?.filter((wellName) =>
                                      wellName?.includes(padName + '_'),
                                    )
                                    ?.map((wellName) => (
                                      <MenuItem
                                        key={wellName}
                                        value={wellName}
                                        style={{ marginLeft: '20px' }}
                                        onClick={() =>
                                          handleWellCheckClick(wellName, padName)
                                        }
                                      >
                                        <Checkbox
                                          checked={
                                            displayWells.indexOf(wellName) > -1
                                          }
                                        />
                                        <ListItemText
                                          primary={wellName?.split('_')?.[1]}
                                        />
                                      </MenuItem>
                                    ))}
                                </AccordionDetails>
                              </Accordion>
                              <Divider />
                            </div>
                          )})}
                        </div>
                      )
                    })}
                    
                    {/* {allPadsMetrics &&
                      Object.keys(allPadsMetrics)?.map((padName, i) => (
                        <div key={padName}>
                          <Typography variant='caption'>{allPadsMetrics?.[padName]?.area || 'Area'}</Typography>
                          <Accordion
                            elevation={0}
                            style={{
                              borderRadius: 0,
                              backgroundColor: 'transparent',
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              aria-controls="panel1-content"
                              id="panel1-header"
                            >
                              <MenuItem
                                value={padName}
                                style={{ width: '100%' }}
                                onClick={() => handlePadCheckClick(padName)}
                              >
                                <Checkbox
                                  checked={displayPads.indexOf(padName) > -1}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handlePadCheckClick(padName, true)
                                  }}
                                />
                                <ListItemText primary={padName} />
                              </MenuItem>
                            </AccordionSummary>
                            <AccordionDetails>
                              {allWells
                                ?.filter((wellName) =>
                                  wellName?.includes(padName + '_'),
                                )
                                ?.map((wellName) => (
                                  <MenuItem
                                    key={wellName}
                                    value={wellName}
                                    style={{ marginLeft: '20px' }}
                                    onClick={() =>
                                      handleWellCheckClick(wellName, padName)
                                    }
                                  >
                                    <Checkbox
                                      checked={
                                        displayWells.indexOf(wellName) > -1
                                      }
                                    />
                                    <ListItemText
                                      primary={wellName?.split('_')?.[1]}
                                    />
                                  </MenuItem>
                                ))}
                            </AccordionDetails>
                          </Accordion>
                          <Divider />
                        </div>
                      ))} */}
                  </Select>
                </FormControl>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flexStart',
                  marginBottom: 20,
                }}
              >
                <Typography>X Metric</Typography>
                <FormControl>
                  <Select
                    value={metricX}
                    onChange={(e) => setMetricX(e.target.value)}
                    size="small"
                    style={{ width: '100%', height: 40 }}
                    disabled={isLoadingPadsMetrics}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          backgroundColor:
                            theme?.palette?.elevationOne?.[
                              darkMode ? 'dark' : 'light'
                            ],
                        },
                      },
                    }}
                  >
                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Stage Information
                    </Typography>
                    {allMetrics['Stage Information']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />
                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Pumping Information
                    </Typography>
                    {allMetrics['Pumping Information']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />

                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      ISIP
                    </Typography>
                    {allMetrics['ISIP']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />

                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Stepdown Model
                    </Typography>
                    {allMetrics['Stepdown Model']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />

                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Frac Model
                    </Typography>
                    {allMetrics['Frac Model']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />
                  </Select>
                </FormControl>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flexStart',
                  marginBottom: 20,
                }}
              >
                <Typography>Y Metric</Typography>
                <FormControl>
                  <Select
                    value={metricY}
                    onChange={(e) => setMetricY(e.target.value)}
                    size="small"
                    style={{ width: '100%', height: 40 }}
                    disabled={isLoadingPadsMetrics}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          backgroundColor:
                            theme?.palette?.elevationOne?.[
                              darkMode ? 'dark' : 'light'
                            ],
                        },
                      },
                    }}
                  >
                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Stage Information
                    </Typography>
                    {allMetrics['Stage Information']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />
                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Pumping Information
                    </Typography>
                    {allMetrics['Pumping Information']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />

                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      ISIP
                    </Typography>
                    {allMetrics['ISIP']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />

                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Stepdown Model
                    </Typography>
                    {allMetrics['Stepdown Model']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />

                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Frac Model
                    </Typography>
                    {allMetrics['Frac Model']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />

                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Induced Seismicity
                    </Typography>
                    {allMetrics['Seismicity']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />
                  </Select>
                </FormControl>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flexStart',
                  marginBottom: 20,
                }}
              >
                <Typography>Z Metric</Typography>
                <FormControl>
                  <Select
                    value={metricZ}
                    onChange={(e) => setMetricZ(e.target.value)}
                    size="small"
                    style={{ width: '100%', height: 40 }}
                    disabled={isLoadingPadsMetrics}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          backgroundColor:
                            theme?.palette?.elevationOne?.[
                              darkMode ? 'dark' : 'light'
                            ],
                        },
                      },
                    }}
                  >
                    <MenuItem
                      value={'None'}
                      key="None"
                    >
                      {allMetrics.None[0]}
                    </MenuItem>
                    <Divider />
                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Stage Information
                    </Typography>
                    {allMetrics['Stage Information']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />
                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Pumping Information
                    </Typography>
                    {allMetrics['Pumping Information']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />

                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      ISIP
                    </Typography>
                    {allMetrics['ISIP']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />

                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Stepdown Model
                    </Typography>
                    {allMetrics['Stepdown Model']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />

                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Frac Model
                    </Typography>
                    {allMetrics['Frac Model']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />
                  </Select>
                </FormControl>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flexStart',
                  marginBottom: 20,
                }}
              >
                <Typography>Size Metric</Typography>
                <FormControl>
                  <Select
                    value={metricSize}
                    onChange={(e) => setMetricSize(e.target.value)}
                    size="small"
                    style={{ width: '100%', height: 40 }}
                    disabled={isLoadingPadsMetrics}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          backgroundColor:
                            theme?.palette?.elevationOne?.[
                              darkMode ? 'dark' : 'light'
                            ],
                        },
                      },
                    }}
                  >
                    <MenuItem value={'None'}>None</MenuItem>
                    <Divider />
                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Stage Information
                    </Typography>
                    {allMetrics['Stage Information']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />
                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Pumping Information
                    </Typography>
                    {allMetrics['Pumping Information']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />

                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      ISIP
                    </Typography>
                    {allMetrics['ISIP']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />

                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Stepdown Model
                    </Typography>
                    {allMetrics['Stepdown Model']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />

                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Frac Model
                    </Typography>
                    {allMetrics['Frac Model']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />
                  </Select>
                </FormControl>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  marginBottom: 20,
                }}
              >
                <div style={{ width: '48%' }}>
                  <FormControl style={{ width: '100%' }}>
                    <Typography>X Axis Type</Typography>
                    <ToggleButtonGroup
                      value={xAxisType}
                      exclusive
                      onChange={(e, newVal) => newVal && setXAxisType(newVal)}
                      size="small"
                      fullWidth
                    >
                      <ToggleButton
                        value="linear"
                        size="small"
                        color={darkMode ? '' : 'primary'}
                      >
                        <Typography style={{ fontSize: '14px' }}>
                          Linear
                        </Typography>
                      </ToggleButton>
                      <ToggleButton
                        value="log"
                        size="small"
                        color={darkMode ? '' : 'primary'}
                      >
                        <Typography style={{ fontSize: '14px' }}>
                          Log
                        </Typography>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </FormControl>
                </div>
                <div style={{ width: '48%' }}>
                  <FormControl style={{ width: '100%' }}>
                    <Typography>Y Axis Type</Typography>
                    <ToggleButtonGroup
                      value={yAxisType}
                      exclusive
                      onChange={(e, newVal) => newVal && setYAxisType(newVal)}
                      size="small"
                      fullWidth
                    >
                      <ToggleButton
                        value="linear"
                        size="small"
                        color={darkMode ? '' : 'primary'}
                      >
                        <Typography style={{ fontSize: '14px' }}>
                          Linear
                        </Typography>
                      </ToggleButton>
                      <ToggleButton
                        value="log"
                        size="small"
                        color={darkMode ? '' : 'primary'}
                      >
                        <Typography style={{ fontSize: '14px' }}>
                          Log
                        </Typography>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </FormControl>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  marginBottom: 20,
                }}
              >
                <FormControl style={{ width: '100%' }}>
                  <Typography>Show Well Trendlines</Typography>
                  <ToggleButtonGroup
                    value={showWellTrendlines}
                    exclusive
                    onChange={(e, newVal) =>
                      newVal && setShowWellTrendlines(newVal)
                    }
                    size="small"
                    fullWidth
                  >
                    <ToggleButton
                      value={'show'}
                      size="small"
                      color={darkMode ? '' : 'primary'}
                    >
                      <Typography style={{ fontSize: '14px' }}>Show</Typography>
                    </ToggleButton>
                    <ToggleButton
                      value={'hide'}
                      size="small"
                      color={darkMode ? '' : 'primary'}
                    >
                      <Typography style={{ fontSize: '14px' }}>Hide</Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </FormControl>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  marginBottom: 20,
                }}
              >
                <FormControl style={{ width: '100%' }}>
                  <Typography>Show CDF</Typography>
                  <ToggleButtonGroup
                    value={showCDF}
                    exclusive
                    onChange={(e, newVal) => newVal && setShowCDF(newVal)}
                    size="small"
                    fullWidth
                  >
                    <ToggleButton
                      value={'show'}
                      size="small"
                      color={darkMode ? '' : 'primary'}
                    >
                      <Typography style={{ fontSize: '14px' }}>Show</Typography>
                    </ToggleButton>
                    <ToggleButton
                      value={'hide'}
                      size="small"
                      color={darkMode ? '' : 'primary'}
                    >
                      <Typography style={{ fontSize: '14px' }}>Hide</Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </FormControl>
              </div>
            </Grid>

            <Grid
              item
              xs={12}
              sm={12}
              md={8}
              lg={9}
              order={downMedium ? 1 : 2}
              sx={{
                [theme.breakpoints.up('sm')]: { height: '100%' },
              }}
            >
              {!allPadsMetrics || isLoadingPadsMetrics ? (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Typography style={{ marginBottom: 20 }}>
                    Loading pad metrics...
                  </Typography>
                  <CircularProgress></CircularProgress>
                </div>
              ) : (
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  {metricZ !== 'None' &&
                    !downMedium &&
                    zMinMax?.[0] >= 0 &&
                    zMinMax?.[1] >= 0 && (
                      <RTFODataAnalyticsLegend
                        zMinMax={zMinMax}
                        setZMinMax={setZMinMax}
                        metric={metricZ}
                        darkMode={darkMode}
                      />
                    )}
                  <RTFODataAnalyticsChart
                    displayPads={displayPads}
                    displayWells={displayWells}
                    allPadsMetrics={allPadsMetrics}
                    metricX={getMetricParamName(metricX)}
                    metricY={getMetricParamName(metricY)}
                    metricZ={getMetricParamName(metricZ)}
                    metricSize={getMetricParamName(metricSize)}
                    zMinMax={zMinMax}
                    sizeMinMax={sizeMinMax}
                    xAxisType={xAxisType}
                    yAxisType={yAxisType}
                    showWellTrendlines={showWellTrendlines}
                    showCDF={showCDF}
                  />
                </div>
              )}
            </Grid>
          </Grid>
        ) : (
          <div>
            <Typography variant="body2">No data selected</Typography>
          </div>
        )}
      </div>
    </Paper>
  )

  function handlePadCheckClick(padName, padOnly) {
    if (padName === 'selectAll') {
      // Select all pads and wells if "Select All" is clicked
      setDisplayPads(allPadsMetrics ? Object.keys(allPadsMetrics) : [])
      setDisplayWells(allWells)
    } else if (padName === 'deselectAll') {
      // Deselect all pads and wells if "Deselect All" is clicked
      setDisplayPads([])
      setDisplayWells([])
    } else if (!displayPads.some((pad) => pad === padName)) {
      // If the pad is not already selected, select it
      // Note: We no longer automatically select all wells within the pad to decouple pad and well selection
      setDisplayPads((prev) => [...prev, padName])
      if (!padOnly) {
        setDisplayWells((prev) => [
          ...prev,
          ...allWells.filter(
            (w) => w.includes(padName) && w.split('_')[0] === padName,
          ),
        ])
      }
    } else {
      // If the pad is already selected, deselect it and its wells
      setDisplayPads((prev) => prev.filter((p) => p !== padName))
      setDisplayWells((prev) =>
        prev.filter(
          (w) => !(w.includes(padName) && w.split('_')[0] === padName),
        ),
      )
    }
  }

  function handleWellCheckClick(wellName, padName) {
    if (!displayWells.includes(wellName)) {
      // If the well is not already selected, select it
      setDisplayWells((prev) => [...prev, wellName])

      // Optionally, select its pad if not already selected
      // This maintains a logical link between wells and their pads without strictly coupling their selection states
      if (!displayPads.includes(padName)) {
        setDisplayPads((prev) => [...prev, padName])
      }
    } else {
      // If the well is already selected, deselect it
      const newDisplayWells = displayWells.filter((w) => w !== wellName)
      setDisplayWells(newDisplayWells)

      // Check if there are no more selected wells in the pad
      // Unlike the original behavior, we do not automatically deselect the pad here
    }
  }
}

const RTFODataAnalyticsLegend = ({ zMinMax, setZMinMax, metric, darkMode }) => {
  const [editColorMin, setEditColorMin] = useState(false)
  const [editColorMax, setEditColorMax] = useState(false)
  const [minColorValue, setMinColorValue] = useState(zMinMax?.[0] || 0)
  const [maxColorValue, setMaxColorValue] = useState(zMinMax?.[1] || 1)

  try {
    if (
      isNaN(zMinMax?.[0]) ||
      !isFinite(zMinMax?.[0]) ||
      isNaN(zMinMax?.[1]) ||
      !isFinite(zMinMax?.[1]) ||
      !metric
    )
      return <></>

    const colorScale = scaleSequential(interpolateTurbo).domain([
      zMinMax?.[0],
      zMinMax?.[1],
    ])

    let grades = []
    let colors = []

    const steps = 10
    const interval = (zMinMax?.[1] - zMinMax?.[0]) / steps
    for (let i = 0; i < steps; i++) {
      const value = zMinMax?.[0] + i * interval

      if (!value || isNaN(value)) continue

      grades.push(value?.toFixed(2))
      colors.push(colorScale(value))
    }

    return (
      <div
        style={{
          width: 150,
          height: 'auto',
          background: !darkMode
            ? 'rgba(255, 255, 255, 0.8)'
            : 'rgba(0, 0, 0, 0.4)',
          zIndex: 420,
          padding: '10px',
          borderRadius: '5px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
          position: 'absolute',
          right: 60,
          top: 90,
        }}
      >
        <Typography
          style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}
        >
          Metrics Z Legend
        </Typography>

        {grades?.map((grade, i) => {
          return i === 0 && editColorMin ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: 10,
              }}
              key={grade + i}
            >
              <TextField
                size="small"
                value={minColorValue}
                style={{ width: 75 }}
                inputProps={{
                  style: {
                    height: '16px',
                    fontSize: '12px',
                  },
                }}
                key={grade + i}
                onChange={(e) => setMinColorValue(e.target.value)}
                onFocus={(event) => {
                  event.target.select()
                }}
              />
              <IconButton
                onClick={() => {
                  setMinColorValue(zMinMax?.[0])
                  setEditColorMin(false)
                }}
              >
                <Cancel style={{ fontSize: '16px' }} />
              </IconButton>
              <IconButton
                onClick={() => {
                  setZMinMax((prev) => [parseFloat(minColorValue), prev?.[1]])
                  setEditColorMin(false)
                }}
              >
                <Check style={{ fontSize: '16px' }} />
              </IconButton>
            </div>
          ) : i === grades?.length - 1 && editColorMax ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: 10,
              }}
              key={grade + i}
            >
              <TextField
                size="small"
                value={maxColorValue}
                style={{ width: 75 }}
                inputProps={{
                  style: {
                    height: '16px',
                    fontSize: '12px',
                  },
                }}
                key={grade + i}
                onChange={(e) => setMaxColorValue(e.target.value)}
                onFocus={(event) => {
                  event.target.select()
                }}
              />
              <IconButton
                onClick={() => {
                  setMaxColorValue(zMinMax?.[0])
                  setEditColorMax(false)
                }}
              >
                <Cancel style={{ fontSize: '16px' }} />
              </IconButton>
              <IconButton
                onClick={() => {
                  setZMinMax((prev) => [prev?.[0], parseFloat(maxColorValue)])
                  setEditColorMax(false)
                }}
              >
                <Check style={{ fontSize: '16px' }} />
              </IconButton>
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                marginBottom: '5px',
                alignItems: 'center',
                cursor: i === 0 || i === grades?.length - 1 ? 'pointer' : '',
              }}
              key={grade + i}
              onClick={() =>
                i === 0
                  ? setEditColorMin(true)
                  : i === grades?.length - 1
                    ? setEditColorMax(true)
                    : {}
              }
            >
              <div
                style={{
                  width: '15px',
                  height: '15px',
                  marginRight: '5px',
                  borderRadius: '3px',
                  boxShadow: '0px 0px 4px lightgrey',
                  backgroundColor: colors?.[i],
                }}
              ></div>
              <Typography
                style={{ fontSize: '12px' }}
              >{`${grade}${grades?.[i + 1] ? ' - ' + grades?.[i + 1] : '+'} ${getMetricUnits(getMetricParamName(metric))}`}</Typography>
            </div>
          )
        })}
      </div>
    )
  } catch (error) {
    console.log(error)
    return <></>
  }
}
